import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

interface StepInfo {
  label: string;
  done: boolean;
}

interface RoomInfo {
  id: number;
  name: string;
  size: number;
}

interface UserInfo {
  name: string;
  id_number: string;
  room_size: number | null;
}

interface DetailData {
  has_cert: boolean;
  cert_number: string | null;
  status: string;
  steps: StepInfo[];
  room: RoomInfo | null;
  user_info: UserInfo;
}

export default function MyProperty() {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<DetailData | null>(null);
  const [hasProperty, setHasProperty] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [showDecor, setShowDecor] = useState(false);

  useEffect(() => {
    api.get('/property-certificate/detail').then(({ data }) => {
      if (!data) {
        setHasProperty(false);
      } else {
        setDetail(data);
      }
    }).catch(() => {
      setHasProperty(false);
    });
  }, []);

  const receiptNumber = detail?.cert_number
    ? `YMBG${detail.cert_number.replace(/\D/g, '').slice(0, 16).padEnd(16, '0')}`
    : '---';

  const steps = detail?.steps ?? [
    { label: '资料审核', done: false },
    { label: '产权审核', done: false },
    { label: '发放入住函', done: false },
    { label: '完成', done: false },
  ];

  const activeStep = steps.filter(s => s.done).length;

  if (!hasProperty || (detail && !detail.has_cert)) {
    return (
      <div className="max-w-md mx-auto min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <AuthGuard />
        <div className="text-center">
          <div className="text-6xl mb-4">🏠</div>
          <h2 className="text-xl font-bold text-[#333] mb-2">暂无产权信息</h2>
          <p className="text-sm text-[#999] mb-6">您还没有关联的产权信息，请先完成产权申请。</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-[#c4161c] text-white rounded-full text-sm"
          >
            返回
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[url(/images/pingmian_beijing.png)] bg-[length:100%_100%] bg-no-repeat bg-top relative pb-[120px] pt-[340px]">
      <AuthGuard />

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 pt-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
        </div>
        <span></span>
        <button
          onClick={() => navigate('/shenji')}
          className="text-[#ffd700] text-xs border border-[#ffd700] rounded-full px-3 py-1"
        >
          项目详情
        </button>
      </div>

      {/* Status banner */}
      <div className="mt-3">
        <div
          className="flex items-center justify-between px-4 cursor-pointer"
          onClick={() => detail?.has_cert && navigate('/one-card')}
        >
          <div className="flex items-center gap-2">
            <img src="/images/pingmian_ruzhu.png" alt="" className="w-full" /> 
          </div> 
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 px-4 ">
        <div className="mt-3 relative cursor-pointer bg-white text-[#c90505] rounded-xl text-center py-2" onClick={() => navigate("/property-progress")}>
          房产办理进度
        </div>
        <div className="mt-3 relative cursor-pointer bg-white text-[#c90505] rounded-xl text-center py-2" onClick={() => navigate("/settlement-cert")}>
          完结证书
        </div> 
      </div>
      
      {/* Progress steps */}
      <div className="px-4 mt-2">
        <div className="bg-white rounded-xl p-3 shadow">
          {/* Labels */}
          <div className="flex items-center justify-between mb-1">
            {steps.map((step, idx) => (
              <span key={idx} className="text-[10px] text-[#666] flex-1 text-center">{step.label}</span>
            ))}
          </div>
          {/* Icons + lines */}
          <div className="flex items-center">
            {steps.map((step, idx) => (
              <div key={idx} className="flex items-center" style={{ flex: idx < steps.length - 1 ? 1 : 'none' }}>
                <div className="flex-shrink-0">
                  {step.done ? (
                    <img src="/images/pingmian_dui.png" alt="✓" className="w-5 h-5" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-300 bg-gray-100" />
                  )}
                </div>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-[3px] ${idx < activeStep ? 'bg-[#c4161c]' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Receipt number and status */}
      <div className="px-4 mt-3">
        <div className="bg-[url(/images/pingmian_kuang.png)] bg-no-repeat bg-[length:100%_100%] p-2 rounded-lg shadow">
          <div className="flex items-center justify-between mb-3 mx-1">
            <span className="text-white/80 text-xs">产权回执号：<span className="">{receiptNumber}</span></span>
            <span className="text-xs text-white/80 ">
              申请状态：
              <span className={`px-2 py-0.5 rounded text-[#d5040d] text-[10px] ${detail?.has_cert ? 'bg-[#fddb9c]' : 'bg-[#999]'}`}>
                {detail?.status ?? '---'}
              </span>
            </span>
          </div>

          {/* Room type card */}
          {detail?.room && (
            <div className="w-full"> 
              <div className="grid grid-cols-2 gap-3 p-1">   
                  <img src="/images/pingmian_huxing.png" alt="户型图" className="w-full rounded mb-1" />
                  <img src="/images/pingmian_jingzhuang.png" alt="精装" className="w-full rounded" /> 
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 -mt-3 w-full justify-between px-5">
                <button
                  // onClick={() => setShowMap(true)} 
                >
                  <img src={"/images/pingmian_pingmian.png"} className="w-[150px]"/>
                </button>
                <button
                  // onClick={() => setShowDecor(true)} 
                >
                  <img src={"/images/pingmian_zhuangxiu.png"} className="w-[150px]"/>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Personal info */}
      <div className="px-4 mt-3">
        <div className="bg-[url(/images/pingmian_xinxi.png)] bg-no-repeat bg-[length:100%_100%] rounded-xl px-4 py-[30px] shadow"> 
          <div className="space-y-2 mt-3 mx-2">
            <div className="flex items-center">
              <span className="text-[#666] text-xs w-[80px]">代/申请人：</span>
              <span className="text-[#333] text-xs font-bold">{detail?.user_info?.name || '---'}</span>
            </div>
            <div className="flex items-center">
              <span className="text-[#666] text-xs w-[80px]">身份证号：</span>
              <span className="text-[#333] text-xs font-bold">{detail?.user_info?.id_number || '---'}</span>
            </div>
            <div className="flex items-center">
              <span className="text-[#666] text-xs w-[80px]">房型大小：</span>
              <span className="text-[#333] text-xs font-bold">{detail?.user_info?.room_size ? `${detail.user_info.room_size}平方米` : '---'}</span>
            </div>
          </div>
        </div>
      </div> 

      {/* Map Modal */}
      {showMap && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={() => setShowMap(false)}>
          <div className="relative max-w-md w-full" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowMap(false)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center text-gray-600 shadow-lg text-lg z-10"
            >
              &times;
            </button>
            <img src="/images/pingmian_ditu.png" alt="平面图" className="w-full rounded-lg shadow-2xl" />
          </div>
        </div>
      )}

      {/* Decoration Modal */}
      {showDecor && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={() => setShowDecor(false)}>
          <div className="relative max-w-md w-full" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowDecor(false)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center text-gray-600 shadow-lg text-lg z-10"
            >
              &times;
            </button>
            <img src="/images/pingmian_jingzhuang.png" alt="豪华装修" className="w-full rounded-lg shadow-2xl" />
          </div>
        </div>
      )}
    </div>
  );
}
